import AdminHeader from "components/Headers/AdminHeader";
import React, { useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { NotificationContext } from "components/CommonComponents/NotificationProvider";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Form,
  FormGroup,
  Input,
  Label,
  Row,
} from "reactstrap";
import {
  getSignatureById,
  addSignature,
  editSignature,
} from "action/signature";
import { MAX_DOCUMENT_SIZE } from "constants/CommonConstants";

const getInitialFields = () => ({
  signatureName: {
    value: "",
    originalValue: "",
    error: "",
  },
  designation: {
    value: "",
    originalValue: "",
    error: "",
  },
  authorizedPersonName: {
    value: "",
    originalValue: "",
    error: "",
  },
  document: {
    value: null,
    originalValue: null,
    error: "",
  },
  status: {
    value: "ACTIVE",
    originalValue: "ACTIVE",
    error: "",
  },
});

function AddSignature() {
  const authData = JSON.parse(localStorage.getItem("p") || "{}");
  const role = authData?.role;
  const { notify } = useContext(NotificationContext);
  const navigate = useNavigate();
  const { id } = useParams();
  const mode = id ? "edit" : "add";
  const decodedId = id;
  const [formData, setFormData] = useState(getInitialFields());
  const [showImageModal, setShowImageModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showAlternative, setShowAlternative] = useState({
    loader: false,
    error: "",
  });

  useEffect(() => {
    if (mode === "edit" && decodedId) {
      fetchSignatureDetails(decodedId);
    }
  }, [mode, decodedId]);

  const fetchSignatureDetails = async (signatureId) => {
    try {
      setShowAlternative({ loader: true, error: "" });

      const res = await getSignatureById(signatureId);

      if (res?.code === 200) {
        const signatureData = res.data;

        setFormData({
          signatureName: {
            value: signatureData.name || "",
            originalValue: signatureData.name || "",
            error: "",
          },
          designation: {
            value: signatureData.authorizedPersonDesignation || "",
            originalValue: signatureData.authorizedPersonDesignation || "",
            error: "",
          },
          authorizedPersonName: {
            value: signatureData.authorizedPersonName || "",
            originalValue: signatureData.authorizedPersonName || "",
            error: "",
          },
          status: {
            value: signatureData.status || "ACTIVE",
            originalValue: signatureData.status || "ACTIVE",
            error: "",
          },
          document: {
            value: signatureData.signatureFilePath || null,
            originalValue: signatureData.signatureFilePath || null,
            error: "",
          },
        });

        setShowAlternative({ loader: false, error: "" });
      } else {
        setShowAlternative({ loader: false, error: "" });

        const message = Array.isArray(res?.message)
          ? res.message
            .map((err) => (err.Msg ? err.Msg : JSON.stringify(err)))
            .join(", ")
          : res?.message || "Failed to load Signature details!";

        notify("danger", message);
      }
    } catch (error) {
      setShowAlternative({
        loader: false,
        error:
          error?.message || "Something went wrong while fetching signature",
      });
    }
  };
  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: { ...prev[field], value, error: "" },
    }));
  };

  const getValidationError = (field, value) => {
    const trimmedValue = typeof value === "string" ? value.trim() : value;

    switch (field) {
      case "signatureName":
        return !trimmedValue
          ? "Please enter signature name"
          : // : !REGEX.ONLY_ALPHABETS_REGEX.test(trimmedValue)
          //   ? "Signature name can contain only letters and spaces"
          "";

      case "designation":
        return !trimmedValue
          ? "Please enter authorized person designation"
          : // : !REGEX.ONLY_ALPHABETS_REGEX.test(trimmedValue)
          //   ? "Designation can contain only letters and spaces"
          "";

      case "authorizedPersonName":
        return !trimmedValue
          ? "Please enter authorized person name"
          : // : !REGEX.ONLY_ALPHABETS_REGEX.test(trimmedValue)
          //   ? "Name can contain only letters and spaces"
          "";

      case "document":
        if (!value) return "Please upload a signature";
        if (value instanceof File) {
          const allowedTypes = ["image/jpeg", "image/jpg", "image/png"];
          if (!allowedTypes.includes(value.type))
            return "Only JPG, JPEG, PNG formats are allowed";
          if (value.size > MAX_DOCUMENT_SIZE)
            return "File size must be less than 15MB";
        }
        return "";
    }
  };

  const handleOnBlur = (name, value) => {
    const trimmedValue = typeof value === "string" ? value.trim() : value;
    const error = getValidationError(name, trimmedValue);
    setFormData((prev) => ({
      ...prev,
      [name]: {
        ...prev[name],
        value: trimmedValue,
        error,
      },
    }));
  };

  const handleDocumentInputChange = (e) => {
    const file = e.target.files[0];
    const error = getValidationError("document", file);

    if (error) {
      e.target.value = "";
    }

    setFormData((prev) => ({
      ...prev,
      document: {
        value: error ? null : file,
        error: error,
      },
    }));
  };

  const getPreviewUrl = (file) => {
    if (!file) return "";

    if (file instanceof File) {
      return URL.createObjectURL(file);
    }

    if (typeof file === "string") {
      return file;
    }

    return file.mediaPath || "";
  };

  const handleSave = async (e) => {
    e.preventDefault();

    let hasError = false;
    const newFormData = { ...formData };

    // Check required fields
    Object.keys(newFormData).forEach((key) => {
      if (key !== "document" || mode !== "edit") {
        const error = getValidationError(key, newFormData[key].value);
        if (error) {
          newFormData[key] = { ...newFormData[key], error };
          hasError = true;
        }
      }
    });

    if (hasError) {
      setFormData(newFormData);
      return;
    }

    try {
      setIsLoading(true);
      let res;

      if (mode === "edit" && decodedId) {
        const signaturePayload = {};

        const fieldMapping = {
          signatureName: "name",
          designation: "authorizedPersonDesignation",
          authorizedPersonName: "authorizedPersonName",
          status: "status"
        };

        Object.keys(fieldMapping).forEach(key => {
          if (formData[key].value !== formData[key].originalValue) {
            signaturePayload[fieldMapping[key]] = formData[key].value;
          }
        });
        const isDocumentChanged = formData.document.value instanceof File;

        if (Object.keys(signaturePayload).length === 0 && !isDocumentChanged) {
          notify("info", "No changes detected");
          setIsLoading(false);
          return;
        }

        const payloadFormData = new FormData();
        if (Object.keys(signaturePayload).length > 0) {
          payloadFormData.append("signature", JSON.stringify(signaturePayload));
        }

        if (isDocumentChanged) {
          payloadFormData.append("signatureImage", formData.document.value);
        }

        res = await editSignature(decodedId, payloadFormData);
      } else {
        const signaturePayload = {
          name: formData.signatureName.value,
          authorizedPersonDesignation: formData.designation.value,
          authorizedPersonName: formData.authorizedPersonName.value,
          status: formData.status.value,
        };

        const payloadFormData = new FormData();
        payloadFormData.append("signature", JSON.stringify(signaturePayload));

        if (formData.document.value instanceof File) {
          payloadFormData.append("signatureImage", formData.document.value);
        }

        res = await addSignature(payloadFormData);
      }

      if (res?.code === 200) {
        notify(
          "success",
          res?.message ||
          (mode === "edit"
            ? "Signature updated successfully!"
            : "Signature added successfully!"),
        );
        navigate(
          role === "SUPER_ADMIN"
            ? "/super-admin/signatures"
            : "/admin/signatures",
        )
      } else {
        notify(
          "danger",
          res?.message ||
          `Failed to ${mode === "edit" ? "update" : "add"} signature`,
        );
      }
    } catch (error) {
      notify("danger", error?.message || "Something went wrong");
    } finally {
      setIsLoading(false);
    }
  };

  const handleOnCancel = () => {
    setFormData(getInitialFields());
    navigate(
      role === "SUPER_ADMIN"
        ? "/super-admin/signatures"
        : "/admin/signatures",
    )
  };

  return (
    <>
      <AdminHeader />

      <Container fluid className="mt--6">
        <Row className="justify-content-center">
          <Col>
            <Card>
              <CardHeader className="d-flex align-items-center">
                <i
                  className="fa fa-chevron-left mr-2 cursor"
                  onClick={() => navigate(
                    role === "SUPER_ADMIN"
                      ? "/super-admin/signatures"
                      : "/admin/signatures",
                  )}
                />
                <h3 className="mb-0 text-default">
                  {mode === "edit" ? "Edit Signature" : "Add Signature"}
                </h3>
              </CardHeader>

              <CardBody>
                {mode === "edit" && showAlternative.loader ? (
                  <div className="loaderContainer">
                    <i className="fa fa-spinner fa-spin fa-2xl loader" />
                  </div>
                ) : showAlternative.error ? (
                  <div className="loaderContainer text-muted d-flex flex-column">
                    <h4 className="text-muted">No Signature Data Available</h4>
                    <span className="text-danger">{showAlternative.error}</span>
                  </div>
                ) : (
                  <Form onSubmit={handleSave}>
                    <Row>
                      <Col md="4" sm="12">
                        <FormGroup>
                          <Label className="form-control-label">
                            Signature Name
                          </Label>
                          <span className="text-danger"> *</span>
                          <Input
                            type="text"
                            name="signatureName"
                            placeholder="Enter signature name"
                            value={formData.signatureName.value}
                            onChange={(e) =>
                              handleInputChange(e.target.name, e.target.value)
                            }
                            onBlur={(e) =>
                              handleOnBlur(e.target.name, e.target.value)
                            }
                            maxLength={100}
                            className={
                              formData.signatureName.error ? "is-invalid" : ""
                            }
                          />
                          {formData.signatureName.error && (
                            <div className="invalid-feedback d-block">
                              {formData.signatureName.error}
                            </div>
                          )}
                        </FormGroup>
                      </Col>
                      <Col md="4" sm="12">
                        <FormGroup>
                          <Label className="form-control-label">
                            Authorized Person Designation
                          </Label>
                          <span className="text-danger"> *</span>
                          <Input
                            type="text"
                            name="designation"
                            placeholder="Enter authorized person designation"
                            value={formData.designation.value}
                            onChange={(e) =>
                              handleInputChange(e.target.name, e.target.value)
                            }
                            onBlur={(e) =>
                              handleOnBlur(e.target.name, e.target.value)
                            }
                            maxLength={100}
                            className={
                              formData.designation.error ? "is-invalid" : ""
                            }
                          />
                          {formData.designation.error && (
                            <div className="invalid-feedback d-block">
                              {formData.designation.error}
                            </div>
                          )}
                        </FormGroup>
                      </Col>
                      <Col md="4" sm="12">
                        <FormGroup>
                          <Label className="form-control-label">
                            Authorized Person Name
                          </Label>
                          <span className="text-danger"> *</span>
                          <Input
                            type="text"
                            name="authorizedPersonName"
                            placeholder="Enter authorized person name"
                            value={formData.authorizedPersonName.value}
                            onChange={(e) =>
                              handleInputChange(e.target.name, e.target.value)
                            }
                            onBlur={(e) =>
                              handleOnBlur(e.target.name, e.target.value)
                            }
                            className={
                              formData.authorizedPersonName.error
                                ? "is-invalid"
                                : ""
                            }
                            maxLength={100}
                          />
                          {formData.authorizedPersonName.error && (
                            <div className="invalid-feedback d-block">
                              {formData.authorizedPersonName.error}
                            </div>
                          )}
                        </FormGroup>
                      </Col>
                    </Row>
                    <Row>
                      <Col md="4" sm="12">
                        <FormGroup className="profile-photo-container mt-0">
                          <Label className="form-control-label">
                            Upload Signature{" "}
                            <span className="text-danger"> *</span>
                          </Label>

                          <div
                            className={`profile-photo-upload text-center rounded p-3 ${formData.document.error
                              ? "profile-invalid"
                              : "border"
                              }`}
                          >
                            {formData.document.value ? (
                              <div className="position-relative d-inline-block">
                                <img
                                  src={getPreviewUrl(formData.document.value)}
                                  alt="Signature Preview"
                                  className="profile-photo-preview cursor"
                                  onClick={() => setShowImageModal(true)}
                                />

                                <span
                                  className="remove-image"
                                  onClick={() =>
                                    setFormData((prev) => ({
                                      ...prev,
                                      document: {
                                        value: null,
                                        error: "",
                                      },
                                    }))
                                  }
                                >
                                  &times;
                                </span>
                              </div>
                            ) : (
                              <div className="d-flex flex-column align-items-center">
                                <i className="fa fa-cloud-upload fa-2x text-muted mb-2"></i>
                                <p className="mb-0 mt-1 muted-label font-size-sm">
                                  (Accepted formats: PNG, JPG, JPEG - Max 15 MB)
                                </p>
                                <p className="mb-1 muted-label font-size-sm">
                                  Click to upload signature
                                </p>

                                <Label
                                  for="documentInput"
                                  className="choose-image-btn btn btn-simple btn-sm mt-2"
                                >
                                  Choose Signature
                                </Label>
                              </div>
                            )}

                            <Input
                              type="file"
                              id="documentInput"
                              accept=".jpg,.jpeg,.png"
                              className="d-none"
                              onChange={handleDocumentInputChange}
                            />
                          </div>

                          {formData.document.error && (
                            <div className="invalid-feedback d-block">
                              {formData.document.error}
                            </div>
                          )}
                        </FormGroup>
                      </Col>

                      <Col md="4" sm="12">
                        <FormGroup>
                          <Label className="form-control-label">
                            Status <span className="text-danger"> *</span>
                          </Label>

                          <div className="d-flex mt-2">
                            <FormGroup check className="mr-4">
                              <Label check>
                                <Input
                                  type="radio"
                                  name="status"
                                  className="cursor"
                                  checked={formData.status.value === "ACTIVE"}
                                  onChange={() =>
                                    handleInputChange("status", "ACTIVE")
                                  }
                                />
                                Active
                              </Label>
                            </FormGroup>

                            <FormGroup check>
                              <Label check>
                                <Input
                                  type="radio"
                                  name="status"
                                  className="cursor"
                                  checked={formData.status.value === "INACTIVE"}
                                  onChange={() =>
                                    handleInputChange("status", "INACTIVE")
                                  }
                                />
                                Inactive
                              </Label>
                            </FormGroup>
                          </div>
                        </FormGroup>
                      </Col>
                    </Row>
                    <Row className="mt-4">
                      <Col className="text-right">
                        <Button
                          color="secondary"
                          className="mr-2 save-btn-sm"
                          type="button"
                          onClick={handleOnCancel}
                        >
                          Cancel
                        </Button>

                        <Button
                          color="primary"
                          className="btn-deep-blue mr-2 save-btn-sm"
                          type="submit"
                          disabled={isLoading}
                          onClick={handleSave}
                        >
                          {isLoading ? (
                            <i className="fa fa-spinner fa-spin" />
                          ) : (
                            "Save"
                          )}
                        </Button>
                      </Col>
                    </Row>
                  </Form>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
      {showImageModal && (
        <div
          className="image-modal-overlay"
          onClick={() => setShowImageModal(false)}
        >
          <div className="image-modal-content">
            <img
              src={getPreviewUrl(formData.document.value)}
              alt="signature-preview"
            />
          </div>
        </div>
      )}
    </>
  );
}

export default AddSignature;
